import {FC, ReactNode, useEffect, useState} from 'react';
import {toast} from 'react-toastify';
import {userStorage} from '@/utils/services/storage.service';
import {useAppDispatch, useAppSelector} from '@/store/hooks';
import {getErrorMessage} from '@/utils/helpers';
import {useNavigate, useParams, useSearchParams} from 'react-router-dom';
import {STORAGE_KEY} from '@/utils/enums';
import {IOrganizationUserInfo} from '@/utils/types';
import {getUserInfo, getUserSelector} from '@/store/user';
import {userGetNotificationSettings, userUpdateNotificationSettings} from '@/store/user-notificationSetting';
import {userNotificationSettingsInitialData} from '@/utils/constants/notificationSettings';
import {getEventBySlug} from '@/store/organization-event';

interface OrganizationPrivateRouteProps {
  children: ReactNode;
}

const UserPrivateRoute: FC<OrganizationPrivateRouteProps> = ({children}) => {
  const {slug: slugFromUrl} = useParams();
  const [searchParams] = useSearchParams();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const returnUrl = searchParams.get('returnUrl');

  const slug = slugFromUrl && slugFromUrl !== 'undefined' ? slugFromUrl : returnUrl?.replace(/^\//, '');

  const [isLoading, setIsLoading] = useState(true);

  const user: IOrganizationUserInfo | null = useAppSelector(getUserSelector);
  const userId = userStorage.get(STORAGE_KEY.USER_ID) as number;

  useEffect(() => {
    if (slug) {
      userStorage.set(STORAGE_KEY.ORGANIZATION_EVENT_SLUG, slug);
    }

    const getUser = async () => {
      try {
        await dispatch(getUserInfo(userId)).unwrap();
      } catch (error) {
        toast.error(getErrorMessage(error));
        userStorage.remove(STORAGE_KEY.USER_ID);
      } finally {
        setIsLoading(false);
      }
    };

    if (!user && userId) {
      getUser();
    } else {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!userId) return;
    const getSettings = async () => {
      try {
        await dispatch(userGetNotificationSettings(userId)).unwrap();
      } catch (error) {
        if (error.status === 400) {
          try {
            await dispatch(
              userUpdateNotificationSettings({id: userId, reqBody: userNotificationSettingsInitialData})
            ).unwrap();
          } catch (error) {
            toast.error(getErrorMessage(error));
          }
        }
      }
    };

    getSettings();
  }, [user]);

  useEffect(() => {
    const getEvent = async () => {
      if (!slug) return;
      try {
        await dispatch(getEventBySlug(slug)).unwrap();
      } catch (error) {
        toast.error(getErrorMessage(error));
      }
    };

    getEvent();
  }, [slug, dispatch]);

  useEffect(() => {
    if (!isLoading && !user) {
      navigate(`/${slug ?? ''}`);
    }
  }, [isLoading, user]);

  return !isLoading && children;
};

export default UserPrivateRoute;
